/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import { T, useLanguage } from "../../../context/LanguageContext";
import {
  IconDownload,
  IconEye,
  IconSearch,
  IconX,
  IconMapPin,
  IconPointerQuestion,
  IconBuilding,
  IconUser,
  IconCar,
} from "@tabler/icons-react";
import ColumnsFilter from "../../../components/common/ColumnsFilter";
import DropdownFilter from "../../../components/common/DropdownFilter";
import { useFetchLineHaulRequestData } from "../../../apis/fleetApis";
import formatDate from "../../../utilities/helper";
import VehicleCardSkeleton from "../../../components/skeleton/VehicleCardSkeleton";
import NoDataFound from "../../../components/common/NoDataFound";
import useDebounce from "../../../utilities/useDebounce";
import Pagination from "../../../components/common/Pagination";
import EntriesSelector from "../../../components/common/EntriesSelector";
import { handleDownload } from "../../../utilities/downloadHelper";

type LineHaulRequest = {
  id: string;
  requestId: string;
  originWarehouse: string;
  destinationWarehouse: string;
  createdBy: string;
  weightCapacity: string;
  created_at: string;
  assignedCompany: string;
  assignedContactPerson: string;
  assignedCompanyMobile: string;
  assignedCompanyEmail: string;
  assignedCompanyAddress: string;
  driverName: string;
  driverMobile: string;
  vehicleNumber: string;
  driverLicense: string | undefined;
  driverIqmaDoc: string | undefined;
  vehicleDoc: string | undefined;
  status: string;
  active: boolean;
};

type Props = {
  activeRole: string;
  setHeaderActions?: (node: React.ReactNode) => void;
  setHeaderButtons?: (node: React.ReactNode) => void;
};

const TwoCol = ({
  label,
  value,
  className,
}: {
  label: string;
  value: React.ReactNode;
  className?: string;
}) => (
  <div
    className={`text-sm text-[#1E293B] flex items-center justify-between ${className}`}
  >
    <span className="font-normal text-sm text-slate-500"><T>{label}</T>: </span>{" "}
    <span className="font-medium text-slate-800">{value}</span>
  </div>
);

const SectionHeader = ({ icon: Icon, title }: { icon: any; title: string }) => (
  <div className="flex items-center gap-2 border-b border-[#FBD037]/30 pb-2 mb-3">
    <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
      <Icon size={16} className="text-[#212121]" stroke={1.5} />
    </div>
    <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
      <T>{title}</T>
    </p>
  </div>
);

const RequestsCompanies = ({ activeRole, setHeaderButtons }: Props) => {
  const { t } = useLanguage();
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [searchFor, setSearchFor] = useState<string>("");
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const debouncedSearchFor = useDebounce(searchFor, 550);
  const [isOpen, setIsOpen] = useState(false);
  const [lineHaulItem, setLineHaulItem] = useState<LineHaulRequest | null>(
    null,
  );

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [allSelectedData, setAllSelectedData] = useState<LineHaulRequest[]>([]);
  const [showCheckboxes, setShowCheckboxes] = useState<boolean>(false);

  const isSidePanelOpen = isOpen && lineHaulItem !== null;

  const { data, refetch, isLoading, isFetching } = useFetchLineHaulRequestData(
    pageNo,
    limit,
    debouncedSearchFor,
  );

  const handlePaginationFetch = (page: number) => {
    setPageNo(page);
  };

  useEffect(() => {
    if (data?.results) {
      setAllSelectedData((prev) => {
        const filteredPrev = prev.filter((item: any) =>
          selectedRows.includes(item.id),
        );
        const newSelected = data.results.filter(
          (s: any) =>
            selectedRows.includes(s.id) &&
            !filteredPrev.some((item) => item.id === s.id),
        );
        return [...filteredPrev, ...newSelected];
      });
    }
  }, [data?.results, selectedRows]);

  useEffect(() => {
    refetch();
  }, [debouncedSearchFor, pageNo, limit]);

  useEffect(() => {
    refetch();
  }, [activeRole]);

  useEffect(() => {
    if (setHeaderButtons) {
      setHeaderButtons(null);
    }
    return () => {
      if (setHeaderButtons) {
        setHeaderButtons(null);
      }
    };
  }, [setHeaderButtons]);

  const openModal = (vehicle: LineHaulRequest) => {
    setLineHaulItem(vehicle);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setLineHaulItem(null);
  };

  const [columnsData, setColumnsData] = useState([
    { text: "Checkbox", value: "checkbox", isShow: showCheckboxes },
    { text: "S/N", value: "sn", isShow: true },
    { text: "Request ID", value: "requestId", isShow: true },
    { text: "Origin", value: "origin", isShow: true },
    { text: "Destination", value: "destination", isShow: true },
    { text: "Capacity", value: "capacity", isShow: true },
    { text: "Created On", value: "date", isShow: true },
    { text: "Creator", value: "creator", isShow: true },
    { text: "Assigned Company", value: "assignedCompany", isShow: true },
    { text: "Status", value: "status", isShow: true },
    { text: "Actions", value: "actions", isShow: true },
  ]);

  const setHeadings = (value: string | undefined, checked: boolean) => {
    setColumnsData((prev) =>
      prev.map((col) =>
        col.value === value ? { ...col, isShow: checked } : col,
      ),
    );
  };

  const handleSelectRow = (id: string) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === data?.results?.length) {
      setSelectedRows((prev) =>
        prev.filter((id) => !data.results.some((c: any) => c.id === id)),
      );
    } else {
      const newSelections =
        data?.results
          ?.filter((c: any) => !selectedRows.includes(c.id))
          .map((c: any) => c.id) || [];
      setSelectedRows((prev) => [...prev, ...newSelections]);
    }
  };

  const mapDataToExport = (c: any, index: number) => ({
    "S/N": index + 1,
    "Request ID": c.requestId,
    Origin: c.originWarehouse,
    Destination: c.destinationWarehouse,
    Capacity: c.weightCapacity,
    "Created On": formatDate(c.created_at),
    Creator: c.createdBy,
    "Assigned Company": c.assignedCompany,
    Status: c.status,
  });

  return (
    <div className="grid grid-cols-12 gap-4 relative w-full">
      {/* Backdrop overlay for mobile & tablet screen sizes */}
      {isSidePanelOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={closeModal}
        />
      )}

      <div
        className={`overflow-visible mb-6 transition-all duration-300 ${
          isSidePanelOpen ? "col-span-12 lg:col-span-8 xl:col-span-7" : "col-span-12"
        }`}
      >
        {/* Header Row (Filters + Actions Unified) */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-4">
          {/* Left side: Brand badge */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="bg-[#FBD037] text-[#131327] font-black text-sm px-3.5 py-1.5 rounded-xl tracking-wider shadow-sm select-none">
              <T>Requests Companies</T>
            </div>
          </div>

          {/* Right side: Export + Search + Advanced Filters */}
          <div className="flex items-center flex-wrap gap-2.5">
            {/* Sliding Search */}
            <div className="relative flex items-center">
              <div
                onClick={() => {
                  if (!showSearch) {
                    setShowSearch(true);
                  }
                }}
                className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-lg bg-white shadow-sm h-8 ${
                  showSearch
                    ? "w-64 px-2 cursor-default"
                    : "w-8 justify-center cursor-pointer hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95"
                }`}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSearch(!showSearch);
                  }}
                  className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0"
                  title={t("Search")}
                >
                  <IconSearch size={17} stroke={2.5} />
                </button>
                {showSearch && (
                  <>
                    <input
                      type="text"
                      value={searchFor}
                      onChange={(e) => {
                        setSearchFor(e.target.value);
                        setPageNo(1);
                      }}
                      placeholder={t("Search...")}
                      autoFocus
                      className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none"
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSearchFor("");
                        setShowSearch(false);
                      }}
                      className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0"
                      title={t("Clear & Close")}
                    >
                      <IconX size={14} stroke={2.5} />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Export */}
            <button
              onClick={() =>
                handleDownload({
                  selectedRows,
                  allSelectedData,
                  setSelectedRows,
                  setAllSelectedData,
                  showCheckboxes,
                  setShowCheckboxes,
                  setColumnsData,
                  mapDataToExport,
                  fileName: "request_companies.xlsx",
                })
              }
              className={`w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer ${
                showCheckboxes && selectedRows.length === 0
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
              title={t("Export to Excel")}
            >
              <IconDownload size={17} stroke={2.5} />
              {selectedRows.length > 0 && (
                <span className="ml-1 text-xs font-bold">
                  ({selectedRows.length})
                </span>
              )}
            </button>

            {/* Entries limit selector */}
            {data && data.totalPages * limit > 10 && (
              <EntriesSelector
                value={limit}
                options={[
                  10,
                  ...(data.totalPages * limit > 10 ? [20] : []),
                  ...(data.totalPages * limit > 20 ? [30] : []),
                  ...(data.totalPages * limit > 50 ? [50] : []),
                  ...(data.totalPages * limit > 100 ? [100] : []),
                ]}
                onChange={(val) => {
                  setLimit(val);
                  setPageNo(1);
                }}
              />
            )}

            {/* Columns Filter */}
            <ColumnsFilter
              columnsData={columnsData.filter((c) => c.value !== "checkbox")}
              setHeadings={setHeadings}
            />

            {/* Dropdown Filter */}
            <DropdownFilter
              filterheadings={[]}
              triggerClassName="border border-slate-200 bg-white rounded-lg p-1.5 cursor-pointer text-[#212121] hover:bg-slate-50 transition shadow-sm h-8 w-8 flex items-center justify-center duration-150"
              iconSize={17}
            />
          </div>
        </div>

        {/* Card Grid View */}
        <div>
          {isLoading || isFetching ? (
            <VehicleCardSkeleton rows={6} />
          ) : data?.results && data?.results.length > 0 ? (
            <div className={`grid grid-cols-1 md:grid-cols-2 ${isSidePanelOpen ? "xl:grid-cols-3" : "lg:grid-cols-3"} gap-5`}>
              {data?.results.map((v: any, idx: number) => (
                <div
                  key={v.id || idx}
                  onClick={() => handleSelectRow(v.id)}
                  className={`group cursor-pointer rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border p-4 transition-all duration-300 space-y-3 relative ${
                    selectedRows.includes(v.id)
                      ? "border-[#FBD037] ring-1 ring-[#FBD037]"
                      : "border-[#D2D2D2]/70"
                  }`}
                >
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />

                  {/* Header */}
                  <div className="flex items-center justify-between border-b border-[#D2D2D2]/70 pb-2 relative z-10">
                    <div>
                      <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">
                        {v.requestId}
                      </p>
                      <h3 className="text-[#000000] font-semibold text-sm mt-1 line-clamp-2 flex items-start gap-1">
                        <IconMapPin
                          size={16}
                          className="text-slate-400 mt-0.5 flex-shrink-0"
                        />
                        <span className="leading-tight">
                          {v.originWarehouse} ➔ {v.destinationWarehouse}
                        </span>
                      </h3>
                    </div>

                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        v.status === "APPROVE"
                          ? "bg-green-500/15 text-green-500"
                          : v.status === "PENDING"
                            ? "bg-yellow-500/15 text-yellow-500"
                            : "bg-red-500/15 text-red-500"
                      }`}
                    >
                      {v.status === "APPROVE"
                        ? t("Approved")
                        : v.status === "PENDING"
                          ? t("Pending")
                          : t(v.status)}
                    </span>
                  </div>

                  {/* Info Section */}
                  <div className="space-y-2.5 relative z-10 text-slate-700">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500 text-sm"><T>Capacity</T>:</span>
                      <span className="text-[#212121] text-sm font-semibold">
                        {v.weightCapacity}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500 text-sm"><T>Creator</T>:</span>
                      <span
                        className="text-[#212121] text-sm font-medium truncate max-w-[150px]"
                        data-tooltip={v.createdBy}
                      >
                        {v.createdBy}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500 text-sm">
                        <T>Created On</T>:
                      </span>
                      <span className="text-[#212121] text-sm font-medium">
                        {formatDate(v.created_at)}
                      </span>
                    </div>
                    {v.assignedCompany && (
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500 text-sm">
                          <T>Assigned Company</T>:
                        </span>
                        <span
                          className="text-slate-700 text-sm font-medium truncate max-w-[150px]"
                          data-tooltip={v.assignedCompany}
                        >
                          {v.assignedCompany}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Action Button */}
                  <div className="pt-2 relative z-10">
                    <button
                      className="w-full h-10 rounded-lg bg-[#FBD037]/20 text-[#000000] text-sm font-semibold hover:bg-[#FBD037]/30 transition-colors flex items-center justify-center gap-1.5"
                      onClick={(e) => {
                        e.stopPropagation();
                        openModal(v);
                      }}
                    >
                      <IconEye size={16} />
                      <T>View Details</T>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <NoDataFound />
          )}
        </div>

        <div className="py-4 flex justify-center">
          {data?.totalPages > 1 && (
            <Pagination
              totalPages={data?.totalPages}
              currentPage={pageNo}
              fetchData={handlePaginationFetch}
            />
          )}
        </div>
      </div>

      {/* Side Panel for Request Details */}
      {isSidePanelOpen && (
        <div className="fixed top-20 bottom-0 right-0 z-50 w-full sm:max-w-2xl lg:max-w-none bg-white/20 p-4 sm:p-6 lg:p-0 lg:static lg:col-span-4 xl:col-span-5 lg:top-auto lg:bottom-auto animate-slide-left flex flex-col h-[calc(100vh-80px)] lg:h-full rounded-l-3xl lg:rounded-3xl border border-[#D2D2D2]/50 shadow-sm overflow-hidden bg-white lg:bg-transparent">
          {/* Sidebar Header */}
          <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-[#212121]">
              <T>Request Details</T>
            </h2>
            <button
              onClick={closeModal}
              className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-slate-50 transition"
            >
              <IconX size={20} />
            </button>
          </div>

          {/* Sidebar Scrollable Body */}
          <div className="w-full py-4 px-4 overflow-y-auto">
            <div className="space-y-4 pb-6">
              {/* Request Info */}
              <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                <SectionHeader
                  icon={IconPointerQuestion}
                  title="Request Info"
                />

                <div className="space-y-4">
                  <TwoCol
                    label="Origin"
                    value={lineHaulItem?.originWarehouse || ""}
                  />
                  <TwoCol
                    label="Destination"
                    value={lineHaulItem?.destinationWarehouse || ""}
                  />
                  <TwoCol
                    label="Creator"
                    value={lineHaulItem?.createdBy || ""}
                  />
                  <TwoCol
                    label="Requested Date"
                    value={formatDate(lineHaulItem?.created_at || "")}
                  />
                  <TwoCol
                    label="Capacity"
                    value={lineHaulItem?.weightCapacity || ""}
                  />
                  <TwoCol
                    label="Assigned Company"
                    value={lineHaulItem?.assignedCompany || ""}
                  />
                </div>
              </div>

              {/* Company Details */}
              {lineHaulItem?.assignedCompany && (
                <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                  <SectionHeader icon={IconBuilding} title="Company Details" />
                  <div className="space-y-4">
                    <TwoCol
                      label="Name"
                      value={lineHaulItem?.assignedCompany || ""}
                    />
                    <TwoCol
                      label="Contact Person"
                      value={lineHaulItem?.assignedContactPerson || ""}
                    />
                    <TwoCol
                      label="Mobile"
                      value={lineHaulItem?.assignedCompanyMobile || ""}
                    />
                    <TwoCol
                      label="Email"
                      value={lineHaulItem?.assignedCompanyEmail || ""}
                    />
                    <TwoCol
                      className="col-span-2"
                      label="Address"
                      value={lineHaulItem?.assignedCompanyAddress || ""}
                    />
                  </div>
                </div>
              )}

              {/* Driver Details */}
              {(lineHaulItem?.driverName || lineHaulItem?.driverMobile) && (
                <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                  <SectionHeader icon={IconUser} title="Driver Details" />

                  <div className="space-y-4">
                    <TwoCol
                      label="Driver Name"
                      value={lineHaulItem?.driverName || ""}
                    />
                    <TwoCol
                      label="Mobile Number"
                      value={lineHaulItem?.driverMobile || ""}
                    />
                    <TwoCol
                      label="License"
                      value={
                        <div className="flex items-center gap-1">
                          {lineHaulItem?.driverLicense && (
                            <img
                              onClick={() => {
                                const url = lineHaulItem?.driverLicense;
                                if (typeof url === "string") {
                                  const link = document.createElement("a");
                                  link.href = url;
                                  link.target = "_blank";
                                  link.download = "";
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }
                              }}
                              className="cursor-pointer hover:opacity-80"
                              title={t("Download License")}
                              src="/img/download-file-icon.svg"
                              alt="Download"
                            />
                          )}
                        </div>
                      }
                    />
                    <TwoCol
                      label="Iqama"
                      value={
                        <div className="flex items-center gap-1">
                          {lineHaulItem?.driverIqmaDoc && (
                            <img
                              onClick={() => {
                                const url = lineHaulItem?.driverIqmaDoc;
                                if (typeof url === "string") {
                                  const link = document.createElement("a");
                                  link.href = url;
                                  link.target = "_blank";
                                  link.download = "";
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }
                              }}
                              className="cursor-pointer hover:opacity-80"
                              title={t("Download Iqama")}
                              src="/img/download-file-icon.svg"
                              alt="Download"
                            />
                          )}
                        </div>
                      }
                    />
                  </div>
                </div>
              )}

              {/* Vehicle Details */}
              {(lineHaulItem?.vehicleNumber || lineHaulItem?.vehicleDoc) && (
                <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                  <SectionHeader icon={IconCar} title="Vehicle Details" />

                  <div className="space-y-4">
                    <TwoCol
                      label="Vehicle Number"
                      value={lineHaulItem?.vehicleNumber || ""}
                    />
                    <TwoCol
                      label="Vehicle Doc"
                      value={
                        <div className="flex items-center gap-1">
                          {lineHaulItem?.vehicleDoc && (
                            <img
                              onClick={() => {
                                const url = lineHaulItem?.vehicleDoc;
                                if (typeof url === "string") {
                                  const link = document.createElement("a");
                                  link.href = url;
                                  link.target = "_blank";
                                  link.download = "";
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }
                              }}
                              className="cursor-pointer hover:opacity-80"
                              title={t("Download Vehicle Doc")}
                              src="/img/download-file-icon.svg"
                              alt="Download"
                            />
                          )}
                        </div>
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestsCompanies;
